import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'compact' | 'wide';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="group flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
      >
        <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={comic.cover}
            alt={comic.title}
            title={`${comic.title} - 洞洞杂货铺漫画`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
            {comic.title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
            </span>
            <span>{comic.status}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'wide') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
      >
        <div className="w-full sm:w-32 h-44 sm:h-40 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={comic.cover}
            alt={comic.title}
            title={`${comic.title} - 洞洞杂货铺漫画`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <h3 className="text-base font-heading font-bold text-foreground-900 group-hover:text-primary-500 transition-colors">
              {comic.title}
            </h3>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-foreground-500">
              <span>{comic.author}</span>
              <span>|</span>
              <span className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full text-xs">
                {comic.category}
              </span>
              <span>|</span>
              <span className={comic.status === '连载中' ? 'text-accent-500' : 'text-foreground-500'}>
                {comic.status}
              </span>
            </div>
            <p className="text-sm text-foreground-600 mt-3 line-clamp-2">{comic.description}</p>
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-foreground-400 flex-wrap">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-500"></i>
              {comic.rating}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-file-list-3-line"></i>
              {comic.chapters.length}话
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {comic.updateTime}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${comic.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={comic.cover}
          alt={comic.title}
          title={`${comic.title} - 洞洞杂货铺漫画`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            comic.status === '连载中'
              ? 'bg-accent-500 text-background-50'
              : 'bg-foreground-600 text-background-50'
          }`}>
            {comic.status}
          </span>
        </div>
        {comic.featured && (
          <div className="absolute top-2 right-2">
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-background-50">
              推荐
            </span>
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col gap-1.5">
        <h3 className="text-sm font-heading font-bold text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 truncate">{comic.author}</p>
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-1 text-xs text-accent-500">
            <i className="ri-star-fill text-xs"></i>
            <span className="font-medium">{comic.rating}</span>
          </div>
          <span className="text-xs text-foreground-400">
            更新至{comic.chapters.length}话
          </span>
        </div>
      </div>
    </Link>
  );
}